import {
  formatBytes,
  formatDateWithDayAndHour,
  formatHourAndMinute,
  formatNumber,
  formatTimeWithWeekDay,
  tooltipMonthlyDateFormatter,
  xAxisDailyDateFormatter,
  xAxisMonthlyDateFormatter,
} from '~/components/echarts/formatters'

function multipleSeries(data) {
  return Object.keys(data.data).length > 1
}

function emptyData(data) {
  return Object.keys(data.data).length === 0
}

function formatValueWithUnit(value, unit) {
  if (unit === 'Bytes') {
    return formatBytes(value)
  }
  return `${formatNumber(value)} ${unit}`
}

function buildSeries(data) {
  const series = []
  const yAxisIndexes = data.y_axis_indexes || {}

  Object.entries(data.data).map(([name, rows]) => {
    const values = rows.map((row) => {
      return row[1]
    })
    const serie = {
      type: 'line',
      name,
      data: values,
    }
    if (yAxisIndexes[name] != null) {
      serie.yAxisIndex = yAxisIndexes[name]
    }
    if (data.filled) {
      serie.areaStyle = {}
    }
    series.push(serie)
    return values
  })

  // Show an empty chart
  if (emptyData(data)) {
    series.push({
      type: 'line',
      data: [0],
    })
  }

  return series
}

const dataZoom = [
  {
    type: 'slider',
    labelFormatter: (_index, isodateStr) => formatDateWithDayAndHour(isodateStr),
  },
]

export function largeScaleBarChart(data) {
  const unit = data.unit
  const dateFormat = data.x.format
  const limitLinesData = Array.isArray(data.limit_lines) && data.limit_lines.length > 0 ? [...data.limit_lines] : []
  const seriesData = buildSeries(data)

  if (limitLinesData.length > 0) {
    limitLinesData
      .slice()
      .reverse()
      .forEach(({ name, value, color }) => {
        const lineColor = color || '#EE6666'
        seriesData.unshift({
          name,
          type: 'line',
          data: new Array(data.x.values.length).fill(value),
          lineStyle: { type: 'solid', width: 2, color: lineColor },
          itemStyle: { color: lineColor },
          symbol: 'none',
        })
      })
  }

  const hasMultipleSeries = seriesData.length > 1 || multipleSeries(data)
  const showDataZoom = data.data_zoom !== false && !emptyData(data)
  const selected = {}
  const disabledMetrics = data.disabled_metrics || []
  disabledMetrics.forEach((m) => {
    selected[m] = false
  })

  const option = {
    aria: {
      enabled: true,
    },
    // Show the legend on the top to fix the issue from echart 6 that make the legend and date range overlap
    legend: hasMultipleSeries
      ? {
          top: 10,
          left: 'center',
          itemGap: 20,
          selected,
        }
      : {},
    grid: {
      top: hasMultipleSeries ? 50 : 20,
      bottom: emptyData(data) || !showDataZoom ? 20 : 80,
      left: 60,
      right: 20,
    },
    dataZoom: showDataZoom ? dataZoom : [],
    tooltip: {
      padding: 5,
      borderWidth: 1,
      textStyle: { fontSize: 12 },
      trigger: 'axis',
      valueFormatter: data.y_axis ? undefined : (value) => formatValueWithUnit(value, unit),
      axisPointer: {
        type: 'line',
        lineStyle: { type: 'dotted', width: 2 },
        label: {
          formatter: ({ value }) => {
            if (dateFormat === 'monthly') {
              return tooltipMonthlyDateFormatter({ value })
            } else {
              return formatTimeWithWeekDay(value)
            }
          },
        },
      },
    },
    xAxis: {
      data: data.x.values,
      type: 'category',
      axisLabel: {
        formatter: (value) => {
          if (dateFormat === 'hourly') {
            return formatHourAndMinute(value)
          } else if (dateFormat === 'monthly') {
            return xAxisMonthlyDateFormatter(value)
          } else {
            return xAxisDailyDateFormatter(value)
          }
        },
      },
      silent: false,
      splitLine: {
        show: false,
      },
      splitArea: {
        show: false,
      },
    },
    yAxis: createYAxisData(data, unit),
    series: seriesData,
  }

  if (data.y_axis) {
    option.tooltip.formatter = (params) => {
      if (!params || params.length === 0) return ''
      let result = `${params[0].axisValueLabel}<br/>`
      params.forEach((item) => {
        const seriesYAxisIndex = option.series[item.seriesIndex]?.yAxisIndex || 0
        const axisUnit = data.y_axis[seriesYAxisIndex]?.unit || unit
        const value = item.value || 0
        result += `${item.marker} ${item.seriesName}: ${formatValueWithUnit(value, axisUnit)}<br/>`
      })
      return result
    }
  }

  return option
}

function createYAxisData(data, unit) {
  if (data.y_axis) {
    return data.y_axis.map((axis, index) => ({
      type: 'value',
      axisLabel: {
        formatter: (value) => formatValueWithUnit(value, axis.unit),
      },
      splitArea: {
        show: false,
      },
      splitLine: {
        show: index === 0,
      },
      max: axis.max_y_axis_value,
      position: index === 0 ? 'left' : 'right',
    }))
  } else {
    return {
      type: 'value',
      axisLabel: {
        formatter: (value) => formatValueWithUnit(value, unit),
      },
      splitArea: {
        show: false,
      },
      max: data.max_y_axis_value,
    }
  }
}
